/**
 * anonVaultUtils.ts
 * Utilities for reading the anonymous token vault directly.
 * Used for accurate counting and enumeration when the index might be incomplete.
 */

import type { AnonEntity } from "./anonTokenVault";

const DB_NAME = "societea_anon_vault";
const TOKENS = "tokens";
const DB_VERSION = 1;

/**
 * Get actual count of tokens stored in IndexedDB vault
 * This is more accurate than the index, which only tracks explicitly added refs
 */
export async function getActualVaultTokenCount(): Promise<number> {
  try {
    const db = await openVaultDB();
    return new Promise<number>((resolve, reject) => {
      try {
        const tx = db.transaction(TOKENS, "readonly");
        const store = tx.objectStore(TOKENS);
        const countReq = store.count();
        
        countReq.onsuccess = () => resolve(countReq.result);
        countReq.onerror = () => reject(countReq.error);
        tx.onabort = () => reject(tx.error);
      } catch (e) {
        reject(e);
      }
    });
  } catch {
    // Fallback: check localStorage
    return getLocalStorageTokenCount();
  }
}

/**
 * Get all token keys from the vault (entity type and ID)
 * Returns actual stored tokens, not just the index
 */
export async function getAllVaultKeys(): Promise<Array<{ type: AnonEntity; id: string | number }>> {
  try {
    const db = await openVaultDB();
    return new Promise<Array<{ type: AnonEntity; id: string | number }>>((resolve, reject) => {
      try {
        const tx = db.transaction(TOKENS, "readonly");
        const store = tx.objectStore(TOKENS);
        const keys: Array<{ type: AnonEntity; id: string | number }> = [];
        const cursorReq = store.openKeyCursor();
        
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) {
            resolve(keys);
            return;
          }
          
          // Parse key format: "type:id"
          const key = String(cursor.key);
          const [type, id] = key.split(":");
          if (type && id && isValidEntityType(type)) {
            keys.push({
              type: type as AnonEntity,
              id: /^\d+$/.test(id) ? Number(id) : id,
            });
          }
          
          cursor.continue();
        };
        
        cursorReq.onerror = () => reject(cursorReq.error);
        tx.onabort = () => reject(tx.error);
      } catch (e) {
        reject(e);
      }
    });
  } catch {
    // Fallback: scan localStorage
    return getLocalStorageKeys();
  }
}

/**
 * Helper: Open the vault database
 */
function openVaultDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Helper: Check if string is a valid entity type
 */
function isValidEntityType(type: string): boolean {
  return ["impact", "teabate", "comment", "solution", "feedback", "action"].includes(type);
}

/**
 * Fallback: Count tokens in localStorage
 */
function getLocalStorageTokenCount(): number {
  let count = 0;
  const prefix = "societea:anon:";
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix)) {
      count++;
    }
  }
  
  return count;
}

/**
 * Fallback: Get keys from localStorage
 */
function getLocalStorageKeys(): Array<{ type: AnonEntity; id: string | number }> {
  const keys: Array<{ type: AnonEntity; id: string | number }> = [];
  const prefix = "societea:anon:";
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix)) {
      // Remove prefix: "societea:anon:type:id"
      const cleaned = key.substring(prefix.length);
      const [type, id] = cleaned.split(":");
      if (type && id && isValidEntityType(type)) {
        keys.push({
          type: type as AnonEntity,
          id: /^\d+$/.test(id) ? Number(id) : id,
        });
      }
    }
  }
  
  return keys;
}

